/* eslint-disable @angular-eslint/prefer-inject */
import { inject, Injectable } from '@angular/core';
import { map, Observable, tap } from 'rxjs';
import { AbstractDataService, LoggerService } from '@pf/shared-services';
import {
    ContainerCustomFieldsService,
    ContainersService,
} from '@control-tower/platform-wms';
import {
    EntityChangeTrackingDto,
    IChangesSearchParams,
    ICustomField,
    IPagedResult,
    PFWmsEntities,
} from '@pf/shared-common';
import { ContainerStore } from './container.store';
import {
    ContainerCreate,
    ContainerRead,
    ContainerSearchParams,
} from '../../entities/container.dto';
import { Operation } from 'fast-json-patch';
import { asArraySafe } from '@pf/shared-utility';

@Injectable()
export class ContainerDataService extends AbstractDataService<
    ContainerRead,
    ContainerCreate
> {
    private customFieldsService = inject(ContainerCustomFieldsService);
    protected EntityName = PFWmsEntities.Container;
    constructor(
        private service: ContainersService,
        store: ContainerStore,
        logger: LoggerService
    ) {
        super(store, logger);
    }

    protected override withDefaultValues(entity: ContainerRead): ContainerRead {
        entity.parentContainerId = entity.parentContainerId || '';
        entity.rootContainerId = entity.rootContainerId || '';

        if (entity.id === entity.rootContainerId) {
            entity.rootContainerId = null;
        }

        return entity;
    }

    protected createObs$(body: ContainerCreate): Observable<ContainerRead> {
        return this.service.containerCreate({
            containerViewModelCreateBody: body,
        }) as Observable<ContainerRead>;
    }
    protected deleteObs$(id: string): Observable<ContainerRead> {
        return this.service.containerDelete({
            id,
        }) as Observable<ContainerRead>;
    }
    protected undoDeleteObs$(id: string): Observable<ContainerRead> {
        return this.service.containerUndoDelete({
            id,
        }) as Observable<ContainerRead>;
    }
    protected getObs$(id: string): Observable<ContainerRead> {
        return this.service.containerReadById({
            id,
        }) as Observable<ContainerRead>;
    }
    protected updateObs$(
        id: string,
        operations: Operation[]
    ): Observable<ContainerRead> {
        return this.service.containerUpdatePatch({
            id,
            operation: operations,
        }) as Observable<ContainerRead>;
    }
    protected searchObs$(
        searchParams: ContainerSearchParams
    ): Observable<IPagedResult<ContainerRead>> {
        return this.service.containerSearch(searchParams) as Observable<
            IPagedResult<ContainerRead>
        >;
    }

    protected searchChangesObs$(
        searchParams: IChangesSearchParams
    ): Observable<IPagedResult<EntityChangeTrackingDto<ContainerRead>>> {
        return this.service.containerGetChanges(searchParams) as Observable<
            IPagedResult<EntityChangeTrackingDto<ContainerRead>>
        >;
    }

    createMany$(containers: ContainerCreate[]) {
        this._logger.debug(`Creating multiple containers`);
        return this.service
            .containerCreateMultiple({
                containerViewModelCreateBody: containers,
            })
            .pipe(
                map(result => asArraySafe(result.created) as ContainerRead[]),
                tap(containers =>
                    containers.forEach(container =>
                        this.store.add(container, false)
                    )
                )
            );
    }

    assignParentContainer$(containerId: string, parentId: string) {
        return this.service
            .containerSetParent({
                id: containerId,
                containerSetParentViewModel: {
                    parentContainerId: parentId,
                },
            })
            .pipe(
                map(entity => entity as ContainerRead),
                map(this.withDefaultValues.bind(this)),
                tap(this.store.update.bind(this.store))
            ) as Observable<ContainerRead>;
    }

    unassignParentContainer$(containerId: string) {
        return this.service
            .containerRemoveParent({
                id: containerId,
            })
            .pipe(
                map(updatedContainers =>
                    updatedContainers.map(container =>
                        this.withDefaultValues(container as ContainerRead)
                    )
                ),
                tap(updatedContainers =>
                    this.store.upsertMany(updatedContainers)
                ),
                map(() => this.store.get(containerId))
            ) as Observable<ContainerRead>;
    }

    protected override createCustomFieldObs$(
        entityId: string,
        customField: ICustomField
    ): Observable<ICustomField & { id: string }> {
        return this.customFieldsService.containerCreateCustomField({
            entityId,
            customFieldViewModelCreate: {
                key: customField.key,
                value: customField.value,
            },
        }) as Observable<ICustomField & { id: string }>;
    }

    protected override updateCustomFieldObs$(
        entityId: string,
        customFieldId: string,
        customField: ICustomField
    ): Observable<ICustomField & { id: string }> {
        return this.customFieldsService.containerCustomFieldsUpdatePut({
            entityId,
            id: customFieldId,
            customFieldViewModelCreate: {
                key: customField.key,
                value: customField.value,
            },
        }) as Observable<ICustomField & { id: string }>;
    }

    protected override deleteCustomFieldObs$(
        entityId: string,
        customFieldId: string
    ): Observable<ICustomField & { id: string }> {
        return this.customFieldsService.containerDeleteCustomField({
            entityId,
            id: customFieldId,
        }) as Observable<ICustomField & { id: string }>;
    }

    assignSlot$(
        containerId: string,
        slotId: string
    ): Observable<ContainerRead> {
        return this.service
            .containerAssignSlot({
                id: containerId,
                containerAssignSlotViewModel: {
                    slotId,
                },
            })
            .pipe(
                map(result => result.affectedContainer as ContainerRead),
                tap(this.store.update.bind(this.store))
            );
    }
}
